
import CustomerMapPoint from '@/types/CustomerMapPoint';
import Geopoint from '@/types/Geopoint';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import { NearbyTrailerResultType } from '@/types/NearbyTrailerResultType';
import TrailerMapPoint from '@/types/TrailerMapPoint';
import { defineComponent } from 'vue';
import { isNullOrUndefined, trailerType } from '@/shared/helpers';
import { CargoStatus } from '@/types/CargoStatus';

export default defineComponent({
    props: {
        result: {
            required: true,
            type: NearbyTrailerResult,
        },
    },
    computed: {
        timeZone(): string | undefined {
            return new Date()
                .toLocaleTimeString('en-us', { timeZoneName: 'short' })
                .split(' ')[2];
        },
        validTrailerType(): boolean {
            return (
                this.result?.type !==
                    NearbyTrailerResultType.customerLocation &&
                !isNullOrUndefined(this.trailerTypeText) &&
                this.trailerTypeText.length > 0
            );
        },
        trailerTypeText(): string {
            return trailerType(this.result.trailers[0]);
        },
        isCustomer() {
            return (
                this.result.type === NearbyTrailerResultType.customerLocation
            );
        },
        hasExclusivelyDotExpiredTrailers() {
            let hasDotExpiredTrailers = true;

            this.result.trailers.forEach((trailer) => {
                if (trailer.isDotInspectionExpired === false) {
                    hasDotExpiredTrailers = false;
                }
            });

            return hasDotExpiredTrailers && this.isCustomer;
        },
        hasAnyFault() {
            return (
                this.hasDotExpired === true || this.hasCargoSensorError === true
            );
        },
        hasDotExpired(): boolean {
            return this.result.trailers[0].isDotInspectionExpired === true;
        },
        hasCargoSensorError(): boolean {
            return (
                this.result.trailers[0].cargoStatus ===
                    CargoStatus.SENSOR_ERROR ||
                this.result.trailers[0].cargoStatus === CargoStatus.NO_SENSOR
            );
        },
        reservationEndTime(): string | undefined {
            if (this.isReserved) {
                return this.result.trailers[0].reservation?.expiresAt
                    .toLocal()
                    .toFormat('hh:mma')
                    .toLowerCase();
            } else {
                return undefined;
            }
        },
        isReserved(): boolean {
            return (
                this.result.trailers.filter((x) => x.reservation?.isActive)
                    .length > 0
            );
        },
        address(): string {
            const city =
                this.result?.locationAddress?.city?.toLowerCase() ||
                this.result?.locationAddress?.label
                    ?.split(', ')[0]
                    ?.toLowerCase();
            const state =
                this.result?.locationAddress?.state?.toUpperCase() ||
                this.result?.locationAddress?.label
                    ?.split(', ')[1]
                    ?.toUpperCase();

            return `${city}, ${state}`;
        },
        distance(): string {
            return this.result.distance.toFixed(1);
        },
        icon(): string {
            const point =
                this.result?.location || ({ lat: 0, lng: 0 } as Geopoint);
            let pin;
            if (
                this.result?.type === NearbyTrailerResultType.customerLocation
            ) {
                if (this.isReserved) {
                    pin = new TrailerMapPoint(point, this.result).icon;
                } else {
                    pin = new CustomerMapPoint(point, this.result).icon;
                }
            } else {
                pin = new TrailerMapPoint(point, this.result).icon;
            }
            pin.classList.remove('map-icon');
            return pin.outerHTML;
        },
        iconClass(): string {
            if (this.isReserved) {
                return `result-icon-trailer-reserved`;
            } else if (
                this.result?.type === NearbyTrailerResultType.customerLocation
            ) {
                return `result-icon-customer`;
            } else {
                return `result-icon-trailer`;
            }
        },
        name(): string | undefined {
            if (
                this.isReserved &&
                this.result?.type === NearbyTrailerResultType.standaloneTrailer
            ) {
                return `Your Trailer #${this.result.trailers[0].trailerNumber}`;
            } else if (
                this.result?.type === NearbyTrailerResultType.customerLocation
            ) {
                return this.result.locationName?.trim().toLowerCase();
            } else {
                return `Trailer #${this.result.trailers[0].trailerNumber}`;
            }
        },
    },
    methods: {
        showLocationDetails(): void {
            this.$emit('setSelectedResult', this.result);
        },
    },
});
